// The WGSL twin of `../depth.ts` (R2713).
//
// The same picture from the same figures: the light, the dark and the hue
// of the depth light (R3229) blended over the ring or stepped, the
// lanterns, the depth heat look (R2870), the shoal stipple and the edge
// seam near the own hulls. The
// plane tap is `textureLoad`, the `inGrid` guard and the centre cell of the
// ring walk stay an `if`, because `select` evaluates both arms, and the
// hash is the same `u32` arithmetic, which wraps in both languages.

import { DEPTH_ALL_UNIFORMS } from '../depth';
import { PRELUDE_BODY_WGSL } from './prelude';
import { wgslModule } from './module';
import { LANTERN_SLOTS, SEAM_HULL_SLOTS, STIPPLE_MAX } from '../../../depthlook';

const DEPTH_FRAG_WGSL = `
// A cell outside the grid has no band.
fn bandOf(c: vec2i) -> i32 {
  if (!inGrid(c)) { return 0; }
  return i32(textureLoad(uDepth, c, 0).r);
}

// The dark alpha of band b, 1 to 4.
fn bandAlpha(b: i32) -> f32 { return U.uDpAlpha[b - 1]; }

// layers/depth.ts hash32: the integer hash of the stipple pick.
fn dpHash(i: u32) -> u32 {
  var h = (i ^ 0x9e3779b9u) * 2654435761u;
  h ^= h >> 15u;
  h = h * 2246822519u;
  return h ^ (h >> 13u);
}

// The coverage of the stipple dots of cell c0 at p (layers/depth.ts
// stippleDots).
fn stippleCov(p: vec2f, c0: vec2i, b0: i32) -> f32 {
  let per = U.uDpStipPer[b0 - 1];
  let cell = u32(c0.y * U.uGrid.x + c0.x);
  var n = i32(floor(per));
  if (f32(dpHash(cell * 31u + 97u) % 1000u) < fract(per) * 1000.0) { n += 1; }
  let ctr = cellCenter(c0);
  let px = worldPx();
  var cov = 0.0;
  for (var k: i32 = 0; k < ${STIPPLE_MAX}; k++) {
    if (k >= n) { break; }
    let h = dpHash(cell * 31u + u32(k));
    let h2 = dpHash(h + 11u);
    let ang = f32(h % 6283u) / 1000.0;
    let d = sqrt(f32(h2 % 1000u) / 1000.0) * U.uDpStipSpread;
    let q = ctr + vec2f(cos(ang), sin(ang)) * d;
    cov = max(cov, fillCov(U.uDpStipR - length(p - q), px));
  }
  return cov;
}

// layers/depth.ts seamFade: (1 - d / R)² of the nearest own hull.
fn hullNear(p: vec2f) -> f32 {
  var best = 0.0;
  for (var i: i32 = 0; i < ${SEAM_HULL_SLOTS}; i++) {
    if (i >= U.uDpHullN) { break; }
    let h = U.uDpHulls[i / 2];
    var q = h.zw;
    if (i % 2 == 0) { q = h.xy; }
    let t = max(0.0, 1.0 - length(p - q) / U.uDpSeamR);
    best = max(best, t * t);
  }
  return best;
}

// The depth heat look (R2870): the band's own ink, a hard step, and a
// contour on the shallower side of each edge to a deeper band.
fn heatTint(p: vec2f, c0: vec2i, b0: i32) -> vec4f {
  let k = U.uDpHeatInk[b0 - 1];
  let o = vec4f(k.rgb * k.a, k.a);
  let ctr = cellCenter(c0);
  let w = min(U.uDpEdgeW / max(U.uZoom, 1e-6), 0.4 * U.uHexSize);
  let px = worldPx();
  var cov = 0.0;
  for (var i: i32 = 0; i < 6; i++) {
    let c = neighbor(c0, i);
    let b = bandOf(c);
    if (b <= b0) { continue; }
    let n = cellCenter(c) - ctr;
    let hd = 0.5 * length(n);
    let d = hd - dot(p - ctr, n / (2.0 * hd));
    cov = max(cov, lineCov(d - 0.5 * w, 0.5 * w, px));
  }
  return over(o, U.uDpMark, U.uDpEdgeA * cov);
}

// layers/depth.ts lanternCap: the soft cap of the summed light (R3238).
fn lanternCap(s: f32) -> f32 {
  let room = U.uDpLampMax - U.uDpLampA;
  if (s <= U.uDpLampA) { return s; }
  if (room <= 0.0) { return min(s, U.uDpLampMax); }
  let x = (s - U.uDpLampA) / room;
  return U.uDpLampA + room * x / (1.0 + x);
}

// layers/depth.ts lanternAlpha: the summed alpha of the lanterns at p,
// under the soft cap.
fn lanternLight(p: vec2f) -> f32 {
  var sum = 0.0;
  for (var i: i32 = 0; i < ${LANTERN_SLOTS}; i++) {
    if (i >= U.uDpLampN) { break; }
    let l = U.uDpLamps[i];
    let t = length(p - l.xy) / max(l.z, 1e-6);
    if (t >= 1.0) { continue; }
    if (t < U.uDpLampAt) {
      sum += mix(U.uDpLampA, U.uDpLampMidA, t / U.uDpLampAt);
    } else {
      sum += U.uDpLampMidA * (1.0 - t) / (1.0 - U.uDpLampAt);
    }
  }
  return lanternCap(sum);
}

// The tint of a cell with a band: the light, the dark and the hue, then
// the marks.
fn tint(p: vec2f, c0: vec2i, b0: i32) -> vec4f {
  // The blend over the cell and its ring: the three inks, and the band
  // with its gradient for the seam. A cell with no band adds nothing.
  let invR2 = 1.0 / (U.uDpRadius * U.uDpRadius);
  var den = 0.0;
  var na = 0.0;
  var nl = 0.0;
  var nb = 0.0;
  var nh = vec4f(0.0);
  var dDen = vec2f(0.0);
  var dNb = vec2f(0.0);
  if (U.uDpSoft == 1 || U.uDpSeam == 1) {
    for (var i: i32 = 0; i < 7; i++) {
      var c = c0;
      if (i > 0) { c = neighbor(c0, i - 1); }
      let b = bandOf(c);
      if (b == 0) { continue; }
      var k = 0.0;
      var dk = vec2f(0.0);
      kern(p, cellCenter(c), invR2, &k, &dk);
      den += k;
      dDen += dk;
      na += k * bandAlpha(b);
      nl += k * U.uDpLight[b - 1];
      let h = U.uDpHue[b - 1];
      nh += k * vec4f(h.rgb * h.a, h.a);
      nb += k * f32(b);
      dNb += dk * f32(b);
    }
  }
  var a = bandAlpha(b0);
  var l = U.uDpLight[b0 - 1];
  let h0 = U.uDpHue[b0 - 1];
  var hp = vec4f(h0.rgb * h0.a, h0.a);
  if (U.uDpSoft == 1 && den > 1e-6) { a = na / den; l = nl / den; hp = nh / den; }
  // The light adds rgb and no alpha; the dark and the hue lie over it.
  var o = over(vec4f(U.uDpLightInk * l, 0.0), U.uDpInk, a);
  o = hp + o * (1.0 - hp.a);

  if (U.uDpStipple == 1) { o = over(o, U.uDpMark, U.uDpStipA * stippleCov(p, c0, b0)); }

  if (U.uDpSeam == 1 && U.uDpHullN > 0 && den > 1e-6) {
    let lit = hullNear(p);
    if (lit > 0.0) {
      let f = nb / den;
      let g = (dNb - f * dDen) / den;
      let gn = length(g);
      if (gn > 1e-6) {
        let lv = clamp(floor(f) + 0.5, 1.5, 3.5);
        let d = abs(f - lv) / gn;
        let px = worldPx();
        o = over(o, U.uDpMark, U.uDpGlowA * lit * lineCov(d, 0.5 * U.uDpGlowW, px));
        o = over(o, U.uDpMark, U.uDpLineA * lit * lineCov(d, 0.5 * U.uDpLineW, px));
      }
    }
  }
  return o;
}

@fragment fn fs(v: VsOut) -> @location(0) vec4f {
  let p = v.vWorld;
  let c0 = cellAt(p);
  let b0 = bandOf(c0);
  if (U.uDpHeat == 1) {
    if (b0 == 0) { return vec4f(0.0); }
    return heatTint(p, c0, b0);
  }
  var o = vec4f(0.0);
  if (b0 > 0) { o = tint(p, c0, b0); }
  // The lanterns add the dim ink inside the grid, rock too.
  if (U.uDpLampN > 0 && inGrid(c0)) { o = vec4f(o.rgb + U.uDpLightInk * lanternLight(p), o.a); }
  return o;
}
`;

/** The whole depth module, for the parity suite. */
export function depthProgramWgsl(): string {
  return wgslModule({ uniforms: DEPTH_ALL_UNIFORMS, body: depthBodyWgsl() });
}

/** The depth's BODY: the shared prelude body and the fragment entry. */
export function depthBodyWgsl(): string {
  return PRELUDE_BODY_WGSL + DEPTH_FRAG_WGSL;
}
